import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  CalendarToday as CalendarTodayIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import colors from '../theme/colors';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const MotionBox = motion(Box);

function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: 'Karthik',
    email: 'karthik@arthdesignbuild.com',
    phone: '+1 (555) 123-4567',
    location: 'San Francisco, CA',
    role: 'System Administrator',
    department: 'IoT Infrastructure',
    joinDate: 'January 15, 2024',
    bio: 'Passionate about IoT infrastructure management and security. Leading the charge in modernizing our smart device ecosystem.',
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    // Save logic here
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset changes
  };

  const activities = [
    { action: 'Updated sensor configuration', time: '2 hours ago', icon: '⚙️' },
    { action: 'Created new project "Smart Building"', time: '5 hours ago', icon: '📁' },
    { action: 'Added 12 new IoT devices', time: '1 day ago', icon: '📱' },
    { action: 'Resolved critical alert', time: '2 days ago', icon: '✅' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={0}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Circles */}
        <Box
          sx={{
            position: 'absolute',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: -100,
            right: -50,
            filter: 'blur(40px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            bottom: -75,
            left: -25,
            filter: 'blur(40px)',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MotionBox
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '3rem',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  K
                </Avatar>
              </MotionBox>
            </Grid>
            <Grid item xs>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                  {profileData.name}
                </Typography>
                <MotionBox
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                >
                  <VerifiedIcon sx={{ fontSize: 28, color: '#4ade80' }} />
                </MotionBox>
              </Stack>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                {profileData.role} • {profileData.department}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="Administrator"
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    background: 'rgba(74, 222, 128, 0.3)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Grid>
            <Grid item>
              {!isEditing ? (
                <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Edit Profile
                  </Button>
                </MotionBox>
              ) : (
                <Stack direction="row" spacing={1}>
                  <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      sx={{
                        background: 'rgba(74, 222, 128, 0.3)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        fontWeight: 700,
                        '&:hover': {
                          background: 'rgba(74, 222, 128, 0.4)',
                        },
                      }}
                    >
                      Save
                    </Button>
                  </MotionBox>
                  <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        fontWeight: 700,
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.8)',
                          background: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </MotionBox>
                </Stack>
              )}
            </Grid>
          </Grid>
        </Box>
      </MotionPaper>

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={8}>
          <MotionPaper
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: colors.border.main,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Personal Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileData.name}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, name: e.target.value })
                  }
                  InputProps={{
                    startAdornment: <WorkIcon sx={{ mr: 1, color: colors.primary.main }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={profileData.email}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, email: e.target.value })
                  }
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: colors.primary.main }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profileData.phone}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, phone: e.target.value })
                  }
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: colors.primary.main }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={profileData.location}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, location: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <LocationOnIcon sx={{ mr: 1, color: colors.primary.main }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Role"
                  value={profileData.role}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, role: e.target.value })
                  }
                  InputProps={{
                    startAdornment: <WorkIcon sx={{ mr: 1, color: colors.primary.main }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Join Date"
                  value={profileData.joinDate}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <CalendarTodayIcon sx={{ mr: 1, color: colors.primary.main }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  value={profileData.bio}
                  disabled={!isEditing}
                  multiline
                  rows={3}
                  onChange={(e) =>
                    setProfileData({ ...profileData, bio: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </MotionPaper>
        </Grid>

        {/* Recent Activity & Quick Settings */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Recent Activity */}
            <MotionPaper
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: colors.border.main,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Recent Activity
              </Typography>
              <Stack spacing={2}>
                {activities.map((activity, index) => (
                  <MotionBox
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.7 + index * 0.1 }}
                    whileHover={{ x: 4 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        background: colors.background.default,
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          background: colors.background.paper,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>{activity.icon}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {activity.action}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activity.time}
                        </Typography>
                      </Box>
                    </Box>
                  </MotionBox>
                ))}
              </Stack>
            </MotionPaper>

            {/* Quick Settings */}
            <MotionPaper
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: colors.border.main,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Quick Settings
              </Typography>
              <Stack spacing={1.5}>
                {[
                  { icon: <SecurityIcon />, label: 'Security Settings', color: colors.primary.main },
                  { icon: <NotificationsIcon />, label: 'Notifications', color: colors.secondary.main },
                  { icon: <LanguageIcon />, label: 'Language & Region', color: colors.info.main },
                ].map((setting, index) => (
                  <MotionBox
                    key={setting.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
                    whileHover={{ x: 4 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: colors.background.default,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: setting.color,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {setting.icon}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {setting.label}
                      </Typography>
                    </Box>
                  </MotionBox>
                ))}
              </Stack>
            </MotionPaper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Profile;
